import { useTranslation } from 'react-i18next';

import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { DropdownMenu } from '../DropdownMenu';
import type { DatePickerRangeProps, DatePickerSingleProps, FilterSelectProps } from './types';
import { DROPDOWN_SIZE } from '../DropdownMenu/types';

export type { FilterSelectOption } from './types';

/**
 * A multi-select filter component with built-in search functionality and checkboxes.
 * Wrapper around DropdownMenu with filter-style defaults (searchable, multiple, rounded trigger, etc.).
 *
 * @param options - Array of options to display.
 * @param value - Current selected values (array).
 * @param onChange - Callback when selection changes.
 * @param placeholder - Placeholder text when no value is selected.
 * @param searchPlaceholder - Placeholder text for search input.
 * @param noOptionsText - Text to display when no options match the search.
 * @param formatSelectedOptions - Function to format selected options display.
 * @param onSearch - Callback for search input changes.
 * @param className - Additional CSS classes.
 *
 * @example
 * // Options mode
 * <FilterSelect
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' },
 *   ]}
 *   value={['1']}
 *   onChange={(val) => console.log(val)}
 *   searchPlaceholder="Suchen..."
 *   noOptionsText="Keine Optionen"
 * />
 *
 * @example
 * // Date picker mode
 * <FilterSelect
 *   datePicker
 *   placeholder="Datum"
 *   dateValue={date}
 *   onDateChange={setDate}
 * />
 */
export const FilterSelect = <T extends string | number = string | number>(props: FilterSelectProps<T>) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);

    const {
        className,
        closeOnScroll = true,
        eventPreventDefault,
        eventStopPropagation,
        hideCheckmarks,
        keepInitialPanelWidth = true,
        keepPlaceholder = true,
        maxPanelWidth,
        minPanelWidth,
        onOpenChange,
        open,
        optionClassName,
        panelClassName,
        placeholder,
        roundedPanel = false,
        roundedTrigger = true,
        scrollable = false,
        showBorder = false,
        size = DROPDOWN_SIZE.SM,
        triggerClassName,
        triggerWidth,
    } = props;

    const resolvedPlaceholder = placeholder ?? t('filterSelect.placeholder');
    const resolvedTriggerClassName = cn(
        'hover:bg-grey-outline-light hover:text-midnight filter drop-shadow-sm',
        triggerClassName,
    );

    if (props.datePicker) {
        return (
            <DropdownMenu
                className={className}
                datePicker
                onOpenChange={onOpenChange}
                open={open}
                placeholder={resolvedPlaceholder}
                roundedTrigger={roundedTrigger}
                showBorder={showBorder}
                size={size}
                triggerClassName={resolvedTriggerClassName}
                triggerWidth={triggerWidth}
                eventPreventDefault={eventPreventDefault}
                eventStopPropagation={eventStopPropagation}
                {...((props.datePickerMode === 'single'
                    ? {
                          datePickerMode: 'single',
                          dateValue: props.dateValue,
                          onDateChange: props.onDateChange,
                      }
                    : {
                          datePickerMode: 'range',
                          dateValue: props.dateValue,
                          onDateChange: props.onDateChange,
                      }) satisfies DatePickerSingleProps | DatePickerRangeProps)}
            />
        );
    }

    const { formatSelectedOptions, noOptionsText, onChange, onSearch, options, searchPlaceholder, value } = props;

    const resolvedNoOptionsText = noOptionsText ?? t('filterSelect.noOptions');
    const resolvedSearchPlaceholder = searchPlaceholder ?? t('filterSelect.searchPlaceholder');

    const selectedCount = value?.length ?? 0;
    const badge =
        selectedCount > 0 ? (
            <span className="flex items-center justify-center min-w-4 h-4 rounded-full bg-midnight text-white text-[9px] font-bold shrink-0 tabular-nums">
                {selectedCount}
            </span>
        ) : undefined;

    return (
        <DropdownMenu
            badge={badge}
            className={className}
            closeOnScroll={closeOnScroll}
            formatSelectedOptions={formatSelectedOptions}
            hideCheckmarks={hideCheckmarks}
            hideDivider={true}
            keepInitialPanelWidth={keepInitialPanelWidth}
            keepPlaceholder={keepPlaceholder}
            maxPanelWidth={maxPanelWidth}
            minPanelWidth={minPanelWidth}
            multiple={true}
            noOptionsText={resolvedNoOptionsText}
            onChange={
                onChange ? (((newValue: T | T[]) => onChange(newValue as T[])) as (value: T | T[]) => void) : undefined
            }
            onOpenChange={onOpenChange}
            onSearch={onSearch}
            open={open}
            optionClassName={optionClassName}
            options={options}
            panelClassName={panelClassName}
            placeholder={resolvedPlaceholder}
            roundedPanel={roundedPanel}
            roundedTrigger={roundedTrigger}
            scrollable={scrollable}
            searchable={true}
            searchPlaceholder={resolvedSearchPlaceholder}
            showBorder={showBorder}
            size={size}
            triggerClassName={resolvedTriggerClassName}
            triggerWidth={triggerWidth}
            value={value}
            eventPreventDefault={eventPreventDefault}
            eventStopPropagation={eventStopPropagation}
        />
    );
};

FilterSelect.displayName = 'FilterSelect';
